// PATH: src/components/catalog/forms/ProgramDialog.tsx
import * as React from "react";
import { FormControlLabel, Stack, Switch, Tab, Tabs, TextField } from "@mui/material";
import { useProgramsApi } from "../../../api";
import type { Program } from "../../../models/domain";
import { codeTaken, emptyToNull, nextSortOrder, parseOptionalInt } from "../catalogModel";
import { useCatalog } from "../CatalogContext";
import { FormDialog } from "./FormDialog";
import { hasVisualErrors, VisualFields, visualsFrom, visualsPayload, type VisualValues } from "./VisualFields";

export type ProgramTab = "identity" | "visuals";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Programme édité ; absent = création. */
  program?: Program | null;
  initialTab?: ProgramTab;
  /** Après création : id du nouveau programme. */
  onCreated?: (id: string) => void;
}

/** Programme : identité (onglet 1) et visuels de l'univers 3D (onglet 2). */
export const ProgramDialog: React.FC<Props> = ({ open, onClose, program, initialTab = "identity", onCreated }) => {
  const { index } = useCatalog();
  const { createProgram, updateProgram } = useProgramsApi();
  const siblings = index.programs.map((p) => p.program);

  const [tab, setTab] = React.useState<ProgramTab>(initialTab);
  const [name, setName] = React.useState("");
  const [code, setCode] = React.useState("");
  const [country, setCountry] = React.useState("");
  const [locale, setLocale] = React.useState("");
  const [order, setOrder] = React.useState("");
  const [active, setActive] = React.useState(true);
  const [visuals, setVisuals] = React.useState<VisualValues>(visualsFrom(null));
  const [touched, setTouched] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const createdId = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setName(program?.name ?? "");
    setCode(program?.code ?? "");
    setCountry(program?.country ?? (program ? "" : "MA"));
    setLocale(program?.locale ?? (program ? "" : "fr-FR"));
    setOrder(String(program ? program.sort_order ?? 0 : nextSortOrder(siblings)));
    setActive(program?.is_active ?? true);
    setVisuals(visualsFrom(program));
    setTouched(false);
    createdId.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, program?.id]);

  const parsedOrder = parseOptionalInt(order);
  const errors = {
    name: !name.trim() ? "Le nom est requis." : "",
    code: !code.trim() ? "Le code est requis." : codeTaken(siblings, code, program?.id) ? "Ce code est déjà utilisé." : "",
    order: parsedOrder === null ? "Requis." : Number.isNaN(parsedOrder) ? "Un nombre entier." : "",
  };
  const show = (k: keyof typeof errors) => (touched ? errors[k] : "");

  const validate = () => {
    setTouched(true);
    if (errors.name || errors.code || errors.order) {
      setTab("identity");
      return false;
    }
    if (hasVisualErrors(visuals)) {
      setTab("visuals");
      return false;
    }
    return true;
  };

  const submit = async () => {
    const payload = {
      name: name.trim(),
      code: code.trim(),
      country: emptyToNull(country)?.toUpperCase() ?? null,
      locale: emptyToNull(locale),
      is_active: active,
      sort_order: parsedOrder ?? 0,
      ...visualsPayload(visuals),
    };
    if (program) await updateProgram(program.id, payload);
    else createdId.current = (await createProgram(payload)).data?.id ?? null;
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={program ? `Modifier ${program.name}` : "Nouveau programme"}
      context={program ? undefined : "Un programme regroupe ses étapes, ses niveaux et ses matières."}
      entity="program"
      maxWidth="sm"
      submitLabel={program ? "Enregistrer" : "Créer le programme"}
      successMessage={program ? "Programme enregistré." : "Programme créé."}
      validate={validate}
      onSubmit={submit}
      busy={busy}
      onSaved={() => createdId.current && onCreated?.(createdId.current)}
    >
      <Tabs value={tab} onChange={(_, v: ProgramTab) => setTab(v)} sx={{ mb: 2.5, borderBottom: 1, borderColor: "divider" }}>
        <Tab value="identity" label="Identité" />
        <Tab value="visuals" label="Univers 3D" />
      </Tabs>

      {tab === "identity" ? (
        <Stack spacing={1}>
          <TextField
            autoFocus
            label="Nom"
            required
            fullWidth
            value={name}
            placeholder="Programme Marocain"
            onChange={(e) => setName(e.target.value)}
            error={Boolean(show("name"))}
            helperText={show("name") || " "}
            slotProps={{ htmlInput: { dir: "auto" } }}
          />
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              label="Code"
              required
              fullWidth
              value={code}
              placeholder="MA"
              onChange={(e) => setCode(e.target.value)}
              error={Boolean(show("code"))}
              helperText={show("code") || "Identifiant stable, utilisé par les imports."}
              slotProps={{ htmlInput: { className: "mono", spellCheck: false } }}
            />
            <TextField
              label="Rang"
              required
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              error={Boolean(show("order"))}
              helperText={show("order") || "Ordre d'affichage"}
              sx={{ width: 130, flexShrink: 0 }}
              slotProps={{ htmlInput: { inputMode: "numeric" } }}
            />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              label="Pays"
              fullWidth
              value={country}
              placeholder="MA"
              onChange={(e) => setCountry(e.target.value)}
              helperText="Code ISO à 2 lettres"
              slotProps={{ htmlInput: { maxLength: 2, className: "mono", style: { textTransform: "uppercase" } } }}
            />
            <TextField
              label="Langue"
              fullWidth
              value={locale}
              placeholder="fr-FR"
              onChange={(e) => setLocale(e.target.value)}
              helperText="Ex. fr-FR, ar-MA"
              slotProps={{ htmlInput: { className: "mono" } }}
            />
          </Stack>
          <FormControlLabel
            control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
            label="Actif"
          />
        </Stack>
      ) : (
        <VisualFields
          value={visuals}
          onChange={setVisuals}
          uploadPath="embed/programs"
          showErrors={touched}
          onBusyChange={setBusy}
        />
      )}
    </FormDialog>
  );
};

export default ProgramDialog;
